import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Space,
  Modal,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchsizeData } from '../../../../redux/slice/masterSlice';

interface SizeModelProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: {
    size_id?: number;
    size_name?: string;
  } | null;
  isEditMode?: boolean;
}

const SizeModel: React.FC<SizeModelProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        size_name: values.size_name,
        ...(initialValues?.size_id && { size_id: initialValues.size_id }),
      };

      const url = isEditMode ? 'edit-size' : 'add-size';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: payload,
      });

      showSuccess(
        isEditMode ? 'Size updated successfully!' : 'Size created successfully!'
      );
      form.resetFields();

      if (isModalClose) {
        onCancel();
      }
      onCreate(data);
      dispatch(fetchsizeData());
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} size`);
    }
  };

  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        size_name: initialValues.size_name,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, isEditMode, initialValues, form]);

  return (
    <Modal
      title={isEditMode ? 'Edit Size' : 'Add New Size'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="90%"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="size_name"
              className="px-8"
              label={
                <span>
                  Size&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    (Max 255 characters)
                  </span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the size name' },
                { max: 255, message: 'Maximum 255 characters allowed' },
              ]}
            >
              <Input
                showCount
                maxLength={255}
                placeholder="e.g., Medium, Large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
                disabled={isSubmitDisabled()}
              >
                Submit & Create New Size
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SizeModel;
